import React, { useState, useEffect } from 'react';
import { GeneralInsights, EditableInsightItem } from '../types';
import { apiService } from '../services/api';

interface GeneralInsightsEditorProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  initialInsights: GeneralInsights;
  onSave: (updatedInsights: GeneralInsights) => void;
}

type TabType = 'key_learnings' | 'opportunities' | 'challenges';

const GeneralInsightsEditor: React.FC<GeneralInsightsEditorProps> = ({
  isOpen,
  onClose,
  clientId,
  initialInsights,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('key_learnings');
  const [insights, setInsights] = useState<EditableInsightItem[]>([]);
  const [titles, setTitles] = useState({
    key_learnings: '',
    opportunities: '',
    challenges: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Convert GeneralInsights to EditableInsightItem[]
      const editableInsights: EditableInsightItem[] = [];
      
      // Add key learnings
      initialInsights.keyLearnings.items.forEach((item, index) => {
        editableInsights.push({
          ...item,
          category: 'key_learnings',
          order_index: index,
          id: `key_learnings_${index}`
        });
      });
      
      // Add opportunities
      initialInsights.opportunities.items.forEach((item, index) => {
        editableInsights.push({
          ...item,
          category: 'opportunities',
          order_index: index,
          id: `opportunities_${index}`
        });
      });
      
      // Add challenges
      initialInsights.challenges.items.forEach((item, index) => {
        editableInsights.push({
          ...item,
          category: 'challenges',
          order_index: index,
          id: `challenges_${index}`
        });
      });
      
      setInsights(editableInsights);
      setTitles({
        key_learnings: initialInsights.keyLearnings.title,
        opportunities: initialInsights.opportunities.title,
        challenges: initialInsights.challenges.title
      });
      setError(null);
    }
  }, [isOpen, initialInsights]);

  const getTabInsights = (category: TabType) => {
    return insights.filter(insight => insight.category === category && !insight.isDeleted);
  };

  const handleInsightChange = (id: string, field: 'text' | 'emoji', value: string) => {
    setInsights(prev => prev.map(insight => 
      insight.id === id ? { ...insight, [field]: value } : insight
    ));
  };

  const handleTitleChange = (category: TabType, title: string) => {
    setTitles(prev => ({ ...prev, [category]: title }));
  };

  const addInsight = (category: TabType) => {
    const newInsight: EditableInsightItem = {
      id: `temp_${category}_${Date.now()}`,
      text: '',
      emoji: '',
      category,
      order_index: getTabInsights(category).length,
      isNew: true
    };
    setInsights(prev => [...prev, newInsight]);
  };

  const removeInsight = (id: string) => {
    setInsights(prev => prev.map(insight => 
      insight.id === id ? { ...insight, isDeleted: true } : insight
    ));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare insights for API
      const insightsToSave = insights
        .filter(insight => !insight.isDeleted && insight.text.trim())
        .map((insight, index) => ({
          category: insight.category,
          title: titles[insight.category],
          text: insight.text,
          emoji: insight.emoji,
          order_index: index
        }));

      const updatedInsights = await apiService.updateInsights(clientId, insightsToSave);
      onSave(updatedInsights);
      onClose();
    } catch (err) {
      console.error('Failed to save insights:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTabStyle = (tabType: TabType) => ({
    flex: 1,
    padding: '12px 20px',
    borderBottom: activeTab === tabType ? '2px solid #007bff' : '1px solid #e1e5e9',
    color: activeTab === tabType ? '#007bff' : '#6b7280',
    background: activeTab === tabType ? '#f8f9fa' : 'white',
    cursor: 'pointer',
    fontWeight: activeTab === tabType ? 600 : 500,
    fontSize: 14,
    transition: 'all 0.2s ease',
    border: 'none',
    borderRadius: 0,
  });

  const currentTabInsights = getTabInsights(activeTab);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #e1e5e9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111' }}>
            Edit General Insights
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e1e5e9' }}>
          <button style={getTabStyle('key_learnings')} onClick={() => setActiveTab('key_learnings')}>
            Key Learnings
          </button>
          <button style={getTabStyle('opportunities')} onClick={() => setActiveTab('opportunities')}>
            Opportunities
          </button>
          <button style={getTabStyle('challenges')} onClick={() => setActiveTab('challenges')}>
            Challenges
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 28px', maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Title Editor */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Section Title
            </label>
            <input
              type="text"
              value={titles[activeTab]}
              onChange={(e) => handleTitleChange(activeTab, e.target.value)}
              placeholder={`Enter title for ${activeTab === 'key_learnings' ? 'Key Learnings' : activeTab === 'opportunities' ? 'Opportunities' : 'Challenges'}`}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: '500',
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              {activeTab === 'key_learnings' && 'Key insights and learnings from the project.'}
              {activeTab === 'opportunities' && 'Potential opportunities and areas for growth.'}
              {activeTab === 'challenges' && 'Challenges and obstacles encountered.'}
            </p>
            <button
              onClick={() => addInsight(activeTab)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                backgroundColor: '#28a745',
                color: 'white',
              }}
            >
              + Add {activeTab === 'key_learnings' ? 'Learning' : activeTab === 'opportunities' ? 'Opportunity' : 'Challenge'}
            </button>
          </div>

          {/* Insights List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {currentTabInsights.map((insight) => (
              <div
                key={insight.id}
                style={{
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: insight.isNew ? '#f8f9fa' : 'white',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    {activeTab === 'key_learnings' ? 'Learning' : activeTab === 'opportunities' ? 'Opportunity' : 'Challenge'}
                  </label>
                  <button
                    onClick={() => removeInsight(insight.id!)}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                    title="Remove item"
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'end' }}>
                  <div>
                    <textarea
                      value={insight.text}
                      onChange={(e) => handleInsightChange(insight.id!, 'text', e.target.value)}
                      placeholder="Enter the insight text..."
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Emoji
                    </label>
                    <input
                      type="text"
                      value={insight.emoji}
                      onChange={(e) => handleInsightChange(insight.id!, 'emoji', e.target.value)}
                      placeholder="🦄"
                      style={{
                        width: '60px',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                        textAlign: 'center',
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {currentTabInsights.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: '#6b7280',
                fontSize: '14px'
              }}>
                No {activeTab === 'key_learnings' ? 'key learnings' : activeTab} yet. Click "Add" to get started.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #e1e5e9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {error && (
            <div style={{ color: '#dc3545', fontSize: '14px' }}>
              {error}
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #e1e5e9',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: loading ? '#9ca3af' : '#007bff',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInsightsEditor; 